import {
  BarChartIcon,
  EyeIcon,
  HashIcon,
  Loader2Icon,
  PencilIcon,
  PercentIcon,
  SaveIcon,
  TableIcon,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ReportConfigV2 } from "@/common/report-configuration";
import { useRef, useState } from "react";
import { createReportV2, saveReportV2 } from "@/lib/serverComm";
import { useNavigate } from "react-router-dom";
import { ReportActionHandlers, ReportActionMenu } from "./ReportActionMenu";
import EventBus from "@/lib/eventBus";

interface ReportControlsProps {
  viewMode: "chart" | "table";
  showToggle: boolean;
  onViewModeChange: (mode: "chart" | "table") => void;
  onShowCountChange: (show: boolean) => void;
  config: ReportConfigV2;
  id?: string;
  canSave: boolean;
  onSave: () => void;
  onEdit?: () => void;
  allowShowCountChange?: boolean;
  actions: ReportActionHandlers;
  disableNavigationOnSave?: boolean;
}

export const ReportControlsV2: React.FC<ReportControlsProps> = ({
  viewMode,
  showToggle,
  onViewModeChange,
  onShowCountChange,
  config,
  id,
  canSave,
  onSave,
  onEdit,
  allowShowCountChange,
  actions,
  disableNavigationOnSave = false,
}) => {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const timeoutRef = useRef<number>();
  const navigate = useNavigate();
  const saveStartTime = useRef<number>();

  const handleMouseLeave = () => {
    timeoutRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 300);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    saveStartTime.current = Date.now();

    try {
      if (id) {
        await saveReportV2(id, config);
        //Save snapshot here instead of in endpoint logic to do it in the background
        actions.onSaveSnapshot();
        EventBus.$emit("sidebar:refreshReports");
        EventBus.$emit("report:saved", id);
      } else {
        //Initial snapshot will be saved in endpoint logic
        const report = await createReportV2(config);
        EventBus.$emit("sidebar:refreshReports");
        EventBus.$emit("report:saved", report.id);
        // Only navigate if not disabled
        if (!disableNavigationOnSave) {
          navigate(`/areport/${report.id}`);
        }
      }

      onSave();
      const elapsed = Date.now() - (saveStartTime.current || 0);
      if (elapsed < 700) {
        await new Promise((resolve) => setTimeout(resolve, 700 - elapsed));
      }
    } catch (error) {
      console.error("Error saving report:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const showCount = config.chart.showCount;

  return (
    <div className="flex items-center gap-1">
      {canSave && !onEdit && (
        <i className="text-xs text-muted-foreground">(Unsaved Changes)</i>
      )}
      {onEdit ? (
        <button className="p-2 hover:bg-accent rounded-full" onClick={onEdit}>
          <PencilIcon className="h-5 w-5" />
        </button>
      ) : (
        <button
          className={`p-2 ${
            canSave ? "hover:bg-sky-600" : ""
          } rounded-full disabled:opacity-50 disabled:cursor-arrow ${canSave ? "bg-primary" : ""}`}
          onClick={handleSave}
          disabled={isSaving || !canSave}
        >
          {isSaving ? (
            <Loader2Icon className="h-5 w-5 animate-spin" />
          ) : (
            <SaveIcon className={`h-5 w-5 ${canSave ? "text-white" : ""}`} />
          )}
        </button>
      )}
      {showToggle && allowShowCountChange && (
        <div className="flex items-center justify-center space-x-[2px] mx-5">
          <HashIcon
            className={`h-4 w-4 pointer-events-none ${
              showCount ? "text-primary" : "text-muted-foreground"
            }`}
          />
          <Switch
            checked={!showCount}
            onCheckedChange={(checked) => onShowCountChange(!checked)}
            className="data-[state=checked]:bg-secondary data-[state=unchecked]:bg-primary"
            aria-label="Toggle between percentage and count"
          />
          <PercentIcon
            className={`h-4 w-4 pointer-events-none ${
              !showCount ? "text-secondary" : "text-muted-foreground"
            }`}
          />
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="p-2 hover:bg-accent rounded-full"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <EyeIcon className="h-5 w-5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex flex-col items-center justify-center space-y-4 px-4 pb-4">
            <div>View Mode</div>
            {showToggle && (
              <div className="flex items-center justify-center space-x-2">
                <TableIcon
                  className={`h-4 w-4 pointer-events-none ${
                    viewMode === "table"
                      ? "text-secondary"
                      : "text-muted-foreground"
                  }`}
                />
                <Switch
                  checked={viewMode === "chart"}
                  onCheckedChange={(checked) =>
                    onViewModeChange(checked ? "chart" : "table")
                  }
                  className={
                    viewMode === "table"
                      ? "data-[state=unchecked]:bg-secondary"
                      : ""
                  }
                  aria-label="Toggle between chart and table view"
                />
                <BarChartIcon
                  className={`h-4 w-4 pointer-events-none ${
                    viewMode === "chart"
                      ? "text-primary"
                      : "text-muted-foreground"
                  }`}
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <ReportActionMenu id={id} actions={actions} />
    </div>
  );
};
